import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import { supabase } from '../supabaseClient'

function AdminFeedbackView({ onBack, onNavigate }) {
  const [entries] = useState(() => { try { return JSON.parse(localStorage.getItem('tlaw_feedback') || '[]'); } catch { return []; } });
  const [expanded, setExpanded] = useState(null);
  const [addressed, setAddressed] = useState({});

  const submitted = entries.filter(e => !e.skipped);
  const clarityAvgAll = submitted.length ? (submitted.reduce((s, e) => s + (e.clarity || 0), 0) / submitted.length).toFixed(1) : null;
  const confAvgAll    = submitted.length ? (submitted.reduce((s, e) => s + (e.confidence || 0), 0) / submitted.length).toFixed(1) : null;
  const openQs = submitted.filter(e => e.openQuestion?.trim());

  const byChapter = {};
  submitted.forEach(e => {
    if (!byChapter[e.chapterId]) byChapter[e.chapterId] = { title: e.chapterTitle || e.chapterId, entries: [] };
    byChapter[e.chapterId].entries.push(e);
  });
  const chStats = Object.entries(byChapter).map(([id, d]) => {
    const avg = arr => arr.length ? (arr.reduce((s,v)=>s+v,0)/arr.length) : 0;
    const cAvg = avg(d.entries.map(e=>e.clarity));
    const cfAvg = avg(d.entries.map(e=>e.confidence));
    const flag = cAvg < 3.5 ? '🔴' : cAvg < 4.0 ? '🟡' : '🟢';
    const flagText = cAvg < 3.5 ? 'Needs review' : cAvg < 4.0 ? 'Monitor' : 'Good';
    return { id, title: d.title, entries: d.entries, cAvg, cfAvg, flag, flagText };
  });

  const rateColor = v => v >= 4 ? V.sage : v >= 3 ? V.gold : V.rose;

  const exportCSV = () => {
    const rows = [['date','chapter','type','clarity','confidence','open_question']];
    submitted.forEach(e => rows.push([
      (e.date||'').slice(0,10), e.chapterTitle||'', e.type||'', e.clarity||'', e.confidence||'',
      `"${(e.openQuestion||'').replace(/"/g,'""')}"`
    ]));
    const blob = new Blob([rows.map(r=>r.join(',')).join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'tlaw_feedback.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div style={{ padding: '28px 24px', maxWidth: '900px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexWrap: 'wrap', gap: '10px' }}>
        <button style={{ ...btnGhost, fontSize: '12px' }} onClick={onBack}>← Back</button>
        <button style={{ ...btnGold, padding: '9px 18px', fontSize: '12px' }} onClick={exportCSV}>Export Feedback CSV →</button>
      </div>
      <h1 style={{ ...serif, fontSize: '28px', fontWeight: 400, color: V.gold, margin: '0 0 24px' }}>Student Feedback</h1>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '12px', marginBottom: '28px' }}>
        {[
          { label: 'Total Responses', val: submitted.length, color: V.gold, sub: '' },
          { label: 'Avg Clarity', val: clarityAvgAll ? `${clarityAvgAll} / 5` : '—', color: clarityAvgAll ? rateColor(+clarityAvgAll) : V.muted, sub: clarityAvgAll ? (+clarityAvgAll >= 4 ? '✓ Content is clear' : +clarityAvgAll >= 3 ? '⚠ Review suggested' : '✗ Needs rewriting') : '' },
          { label: 'Avg Confidence', val: confAvgAll ? `${confAvgAll} / 5` : '—', color: confAvgAll ? rateColor(+confAvgAll) : V.muted, sub: confAvgAll ? (+confAvgAll >= 4 ? '✓ Students confident' : +confAvgAll >= 3 ? '⚠ Review suggested' : '✗ Needs more support') : '' },
          { label: 'Open Questions', val: openQs.length, color: V.gold, sub: '' },
        ].map((s, i) => (
          <div key={i} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '16px 18px' }}>
            <div style={{ fontSize: '10px', color: V.muted, fontFamily: "'Jost',sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '8px' }}>{s.label}</div>
            <div style={{ ...mono, fontSize: '24px', color: s.color, marginBottom: '4px' }}>{s.val}</div>
            {s.sub && <div style={{ fontSize: '11px', color: s.color, fontFamily: "'Jost',sans-serif" }}>{s.sub}</div>}
          </div>
        ))}
      </div>

      {submitted.length === 0 ? (
        <div style={{ background: V.bg2, borderRadius: '12px', padding: '40px', textAlign: 'center' }}>
          <p style={{ color: V.muted, fontFamily: "'Jost',sans-serif", fontSize: '14px' }}>No feedback submitted yet.</p>
        </div>
      ) : (
        <>
          {/* Chapter table */}
          <div style={{ background: V.bg2, borderRadius: '12px', overflow: 'hidden', marginBottom: '32px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 70px 80px 50px 110px', gap: '8px', padding: '12px 20px', borderBottom: `0.5px solid ${V.border}` }}>
              {['Chapter / Practice', 'Clarity', 'Confidence', 'Count', 'Flag'].map(h => (
                <div key={h} style={{ fontSize: '10px', color: V.muted, fontFamily: "'Jost',sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase' }}>{h}</div>
              ))}
            </div>
            {chStats.map(ch => (
              <div key={ch.id}>
                <div onClick={() => setExpanded(expanded === ch.id ? null : ch.id)}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 70px 80px 50px 110px', gap: '8px', padding: '13px 20px', borderBottom: `0.5px solid ${V.border}`, cursor: 'pointer', background: expanded === ch.id ? 'rgba(201,168,76,0.04)' : 'transparent' }}>
                  <div style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif" }}>{ch.title}</div>
                  <div style={{ ...mono, fontSize: '13px', color: rateColor(ch.cAvg) }}>{ch.cAvg.toFixed(1)}</div>
                  <div style={{ ...mono, fontSize: '13px', color: rateColor(ch.cfAvg) }}>{ch.cfAvg.toFixed(1)}</div>
                  <div style={{ fontSize: '13px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{ch.entries.length}</div>
                  <div style={{ fontSize: '11px', color: V.muted, fontFamily: "'Jost',sans-serif" }}>{ch.flag} {ch.flagText}</div>
                </div>
                {expanded === ch.id && (
                  <div style={{ padding: '16px 20px', background: V.bg3, borderBottom: `0.5px solid ${V.border}` }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: ch.entries.filter(e=>e.openQuestion).length ? '16px' : 0 }}>
                      {[{ label: 'Clarity', vals: ch.entries.map(e=>e.clarity) }, { label: 'Confidence', vals: ch.entries.map(e=>e.confidence) }].map(({ label, vals }) => (
                        <div key={label}>
                          <div style={{ fontSize: '10px', color: V.muted, fontFamily: "'Jost',sans-serif", letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '8px' }}>{label}</div>
                          {[5,4,3,2,1].map(n => {
                            const cnt = vals.filter(v=>v===n).length;
                            const pct = vals.length ? Math.round((cnt/vals.length)*100) : 0;
                            return (
                              <div key={n} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                <span style={{ ...mono, fontSize: '10px', color: V.dim, width: '8px' }}>{n}</span>
                                <div style={{ flex: 1, height: '5px', background: V.bg2, borderRadius: '3px', overflow: 'hidden' }}>
                                  <div style={{ height: '100%', width: `${pct}%`, background: n>=4 ? V.sage : n===3 ? V.gold : V.rose, borderRadius: '3px' }} />
                                </div>
                                <span style={{ ...mono, fontSize: '10px', color: V.dim, width: '16px', textAlign: 'right' }}>{cnt}</span>
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                    {ch.entries.filter(e=>e.openQuestion).map((e, i) => (
                      <div key={i} style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '8px', padding: '12px 16px', marginBottom: '8px' }}>
                        <p style={{ fontSize: '13px', color: V.text, fontFamily: "'Jost',sans-serif", lineHeight: 1.65, margin: '0 0 6px' }}>{e.openQuestion}</p>
                        <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{(e.date||'').slice(0,10)} — anonymous</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* All open questions */}
          {openQs.length > 0 && (
            <div>
              <h2 style={{ ...serif, fontSize: '20px', fontWeight: 400, color: V.text, marginBottom: '16px' }}>All Open Questions</h2>
              {[...openQs].sort((a,b) => new Date(b.date||0) - new Date(a.date||0)).map((e, i) => (
                <div key={i} style={{ background: V.bg3, border: `0.5px solid rgba(201,168,76,0.1)`, borderRadius: '10px', padding: '16px 20px', marginBottom: '10px', opacity: addressed[e.id] ? 0.45 : 1, transition: 'opacity 0.2s' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}>
                    <div style={{ display: 'inline-block', padding: '3px 10px', background: 'rgba(201,168,76,0.08)', border: `0.5px solid ${V.border}`, borderRadius: '12px', fontSize: '10px', color: V.gold, fontFamily: "'Jost',sans-serif", letterSpacing: '0.06em', textTransform: 'uppercase' }}>{e.chapterTitle || e.chapterId}</div>
                    <div style={{ fontSize: '11px', color: V.dim, fontFamily: "'Jost',sans-serif" }}>{(e.date||'').slice(0,10)}</div>
                  </div>
                  <p style={{ fontSize: '14px', color: V.text, fontFamily: "'Jost',sans-serif", lineHeight: 1.7, margin: '0 0 12px', textDecoration: addressed[e.id] ? 'line-through' : 'none' }}>{e.openQuestion}</p>
                  <button style={{ ...btnGhost, padding: '6px 14px', fontSize: '11px' }} onClick={() => setAddressed(a => ({ ...a, [e.id]: !a[e.id] }))}>
                    {addressed[e.id] ? 'Mark as unaddressed' : 'Mark as addressed'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// AUTH SCREEN
// ═══════════════════════════════════════════════════════════════
function AuthScreen({ onAuthSuccess }) {
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [termsAgreed, setTermsAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const inp = {
    width: '100%', boxSizing: 'border-box',
    background: V.bg3, border: `1px solid rgba(201,168,76,0.22)`,
    borderRadius: '8px', padding: '12px 16px',
    color: V.text, fontSize: '14px',
    fontFamily: "'Jost',sans-serif", outline: 'none',
    marginBottom: '12px', display: 'block',
  };
  const lbl = { display: 'block', fontSize: '12px', color: V.muted, fontFamily: "'Jost',sans-serif", marginBottom: '5px', letterSpacing: '0.04em' };
  const btnPrimary = { width: '100%', padding: '13px', background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.5)', borderRadius: '8px', color: V.goldL, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '14px', letterSpacing: '0.06em', fontWeight: 500, marginBottom: '10px' };

  const handleSignup = async () => {
    setError(''); setMessage('');
    if (!firstName.trim()) { setError('Please enter your first name.'); return; }
    if (!email.trim()) { setError('Please enter your email.'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (accessCode.trim().toLowerCase() !== 'foundation2026') {
      setError('Invalid access code. Please check your enrollment email or contact Lynn.');
      return;
    }
    setLoading(true);
    const { error: err } = await supabase.auth.signUp({ email, password, options: { data: { first_name: firstName } } });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setMessage('Account created! Please check your email to confirm your account, then sign in.');
  };

  const handleLogin = async () => {
    setError(''); setMessage('');
    if (!email.trim() || !password) { setError('Please enter your email and password.'); return; }
    setLoading(true);
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError('Invalid email or password.'); return; }
    if (data.session) onAuthSuccess(data.session);
  };

  const handleForgotPassword = async () => {
    if (!email.trim()) { setError('Enter your email above, then click Forgot Password.'); return; }
    setLoading(true);
    await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
    setLoading(false);
    setMessage('Password reset email sent. Check your inbox.');
  };

  return (
    <div style={{ minHeight: '100vh', background: V.bg, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px' }}>
      <div style={{ maxWidth: '420px', width: '100%', background: V.bg2, border: `1px solid rgba(201,168,76,0.2)`, borderRadius: '16px', padding: '40px 32px' }}>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 500, color: V.gold, lineHeight: 1.2 }}>Trade Like a Woman™</div>
          <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, fontStyle: 'italic', marginTop: '6px', letterSpacing: '0.06em' }}>The Foundation</div>
        </div>

        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', fontWeight: 400, color: V.text, marginBottom: '22px', textAlign: 'center' }}>
          {mode === 'login' ? 'Welcome Back' : 'Create Your Account'}
        </div>

        {mode === 'signup' && (
          <div>
            <label style={lbl}>First Name</label>
            <input style={inp} type="text" value={firstName} onChange={e => setFirstName(e.target.value)} placeholder="Your first name" autoComplete="given-name" />
          </div>
        )}

        <div>
          <label style={lbl}>Email</label>
          <input style={inp} type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" autoComplete="email" />
        </div>

        <div>
          <label style={lbl}>Password {mode === 'signup' && <span style={{ color: V.dim, fontSize: '11px' }}>(min. 8 characters)</span>}</label>
          <input style={inp} type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" autoComplete={mode === 'login' ? 'current-password' : 'new-password'} onKeyDown={e => e.key === 'Enter' && mode === 'login' && handleLogin()} />
        </div>

        {mode === 'signup' && (
          <div>
            <label style={lbl}>Access Code <span style={{ color: V.dim, fontSize: '11px' }}>— provided when you enrolled</span></label>
            <input style={inp} type="text" value={accessCode} onChange={e => setAccessCode(e.target.value)} placeholder="Enter access code" autoComplete="off" />
          </div>
        )}

        {error && (
          <div style={{ background: 'rgba(196,122,122,0.08)', border: '1px solid rgba(196,122,122,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: V.rose, fontFamily: "'Jost',sans-serif", lineHeight: 1.5 }}>
            {error}
          </div>
        )}
        {message && (
          <div style={{ background: 'rgba(122,171,138,0.08)', border: '1px solid rgba(122,171,138,0.3)', borderRadius: '8px', padding: '10px 14px', marginBottom: '14px', fontSize: '13px', color: V.sage, fontFamily: "'Jost',sans-serif", lineHeight: 1.5 }}>
            {message}
          </div>
        )}

        {mode === 'signup' && (
          <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', alignItems: 'flex-start', cursor: 'pointer' }}
            onClick={() => setTermsAgreed(a => !a)}>
            <div style={{ width: '18px', height: '18px', borderRadius: '3px', border: `1px solid ${termsAgreed ? 'rgba(122,171,138,0.8)' : 'rgba(201,168,76,0.3)'}`, background: termsAgreed ? 'rgba(122,171,138,0.15)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', fontSize: '11px', color: 'rgba(122,171,138,0.9)' }}>
              {termsAgreed ? '✓' : ''}
            </div>
            <span style={{ fontSize: '12px', color: 'rgba(240,237,230,0.55)', lineHeight: 1.6, fontFamily: "'Jost',sans-serif" }}>
              I have read and agree to the Terms of Service including the confidentiality and non-compete provisions.
            </span>
          </div>
        )}

        <button style={{ ...btnPrimary, opacity: (loading || (mode === 'signup' && !termsAgreed)) ? 0.5 : 1 }} onClick={mode === 'login' ? handleLogin : handleSignup} disabled={loading || (mode === 'signup' && !termsAgreed)}>
          {loading ? 'Please wait…' : mode === 'login' ? 'Sign In →' : 'Create Account →'}
        </button>

        {mode === 'login' && (
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <button onClick={handleForgotPassword} style={{ background: 'none', border: 'none', color: 'rgba(201,168,76,0.55)', fontSize: '12px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", textDecoration: 'underline', textDecorationColor: 'rgba(201,168,76,0.25)', padding: '4px' }}>
              Forgot your password?
            </button>
          </div>
        )}

        <div style={{ textAlign: 'center', marginTop: '8px' }}>
          <button onClick={() => { setMode(m => m === 'login' ? 'signup' : 'login'); setError(''); setMessage(''); }}
            style={{ background: 'none', border: 'none', color: 'rgba(201,168,76,0.65)', fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif", textDecoration: 'underline', textDecorationColor: 'rgba(201,168,76,0.3)', padding: '4px' }}>
            {mode === 'login' ? 'New student? Create account →' : 'Already have an account? Sign in →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MIGRATE LOCALSTORAGE DATA TO SUPABASE (runs once per user)
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
// ADMIN DASHBOARD
// ═══════════════════════════════════════════════════════════════
function AdminDashboard({ session, onNavigate }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentDetail, setStudentDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [grantMsg, setGrantMsg] = useState('');

  useEffect(() => {
    fetchStudents();
  }, []); // eslint-disable-line

  const fetchStudents = async () => {
    setLoading(true);
    const { data, error } = await supabase.from('profiles').select('*, student_progress(*)').order('created_at', { ascending: false });
    setLoading(false);
    if (!error && data) setStudents(data);
  };

  const grantTier2 = async (studentId, studentName) => {
    const { error } = await supabase.from('profiles').update({ tier2_unlocked: true }).eq('id', studentId);
    if (!error) {
      setStudents(s => s.map(st => st.id === studentId ? { ...st, tier2_unlocked: true } : st));
      setGrantMsg(`Tier 2 granted to ${studentName}.`);
      setTimeout(() => setGrantMsg(''), 3000);
    }
  };

  const viewStudentDetail = async (student) => {
    setSelectedStudent(student);
    setDetailLoading(true);
    const [progress, goals, whys, trades, logs] = await Promise.all([
      supabase.from('student_progress').select('*').eq('user_id', student.id),
      supabase.from('student_goals').select('*').eq('user_id', student.id),
      supabase.from('student_whys').select('*').eq('user_id', student.id).order('position'),
      supabase.from('student_trades').select('*').eq('user_id', student.id).order('created_at', { ascending: false }),
      supabase.from('daily_logs').select('*').eq('user_id', student.id).order('log_date', { ascending: false }).limit(30),
    ]);
    setStudentDetail({
      progress: progress.data || [],
      goals: goals.data || [],
      whys: whys.data || [],
      trades: trades.data || [],
      logs: logs.data || [],
    });
    setDetailLoading(false);
  };

  const card = { background: '#14141a', border: '1px solid rgba(201,168,76,0.15)', borderRadius: '10px', padding: '20px', marginBottom: '16px' };
  const th = { textAlign: 'left', fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", paddingBottom: '10px', paddingRight: '16px', fontWeight: 400 };
  const td = { padding: '10px 16px 10px 0', fontSize: '12px', color: '#f0ede6', fontFamily: "'Jost',sans-serif", borderTop: '0.5px solid rgba(240,237,230,0.06)', verticalAlign: 'middle' };
  const btnSm = (color) => ({ padding: '5px 12px', background: `rgba(${color},0.1)`, border: `1px solid rgba(${color},0.35)`, borderRadius: '6px', color: `rgba(${color},1)`, cursor: 'pointer', fontFamily: "'Jost',sans-serif", fontSize: '11px', letterSpacing: '0.04em', whiteSpace: 'nowrap' });

  if (selectedStudent) {
    const chapsDone = (studentDetail?.progress || []).filter(p => p.completed).length;
    return (
      <div style={{ maxWidth: '900px', margin: '0 auto', padding: '32px 24px' }}>
        <button onClick={() => { setSelectedStudent(null); setStudentDetail(null); }}
          style={{ background: 'none', border: 'none', color: 'rgba(201,168,76,0.7)', cursor: 'pointer', fontSize: '13px', fontFamily: "'Jost',sans-serif", marginBottom: '20px', padding: 0 }}>
          ← Back to Student List
        </button>
        <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '28px', fontWeight: 400, color: '#c9a84c', margin: '0 0 4px' }}>
          {selectedStudent.first_name || selectedStudent.email}
        </h2>
        <div style={{ fontSize: '13px', color: 'rgba(240,237,230,0.5)', fontFamily: "'Jost',sans-serif", marginBottom: '24px' }}>
          {selectedStudent.email} · Joined {new Date(selectedStudent.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
        </div>
        {detailLoading ? (
          <div style={{ color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", fontSize: '13px' }}>Loading student data…</div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: '12px', marginBottom: '20px' }}>
              {[
                { lbl: 'Chapters Done', val: chapsDone },
                { lbl: 'Goals', val: studentDetail?.goals?.length || 0 },
                { lbl: 'Trades Logged', val: studentDetail?.trades?.length || 0 },
                { lbl: 'Daily Logs', val: studentDetail?.logs?.length || 0 },
                { lbl: 'Tier 2', val: selectedStudent.tier2_unlocked ? '✓ Unlocked' : 'Locked' },
              ].map(s => (
                <div key={s.lbl} style={{ ...card, padding: '14px', textAlign: 'center', marginBottom: 0 }}>
                  <div style={{ fontSize: '10px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '6px' }}>{s.lbl}</div>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '22px', color: '#c9a84c' }}>{s.val}</div>
                </div>
              ))}
            </div>

            {studentDetail?.whys?.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: '10px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Why I Trade</div>
                {studentDetail.whys.map((w, i) => <div key={i} style={{ fontSize: '13px', color: '#f0ede6', fontFamily: "'Jost',sans-serif", marginBottom: '6px', paddingLeft: '10px', borderLeft: '2px solid rgba(201,168,76,0.3)' }}>{w.why_text}</div>)}
              </div>
            )}

            {studentDetail?.progress?.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: '10px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Chapter Progress</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {studentDetail.progress.map(p => (
                    <div key={p.chapter_id} style={{ padding: '6px 12px', borderRadius: '20px', background: p.completed ? 'rgba(122,171,138,0.1)' : 'rgba(240,237,230,0.04)', border: `1px solid ${p.completed ? 'rgba(122,171,138,0.3)' : 'rgba(240,237,230,0.1)'}`, fontSize: '11px', color: p.completed ? '#7aab8a' : 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif" }}>
                      {p.chapter_id} {p.quiz_score != null ? `· ${p.quiz_score}%` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {studentDetail?.logs?.length > 0 && (
              <div style={card}>
                <div style={{ fontSize: '10px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: '12px' }}>Recent Daily Logs</div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: "'Jost',sans-serif", fontSize: '12px' }}>
                  <thead><tr><th style={th}>Date</th><th style={th}>Opening</th><th style={th}>P&amp;L</th><th style={th}>Notes</th></tr></thead>
                  <tbody>
                    {studentDetail.logs.slice(0, 10).map(l => (
                      <tr key={l.id}>
                        <td style={td}>{new Date(l.log_date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                        <td style={td}>{l.opening_balance ? '$' + Math.round(l.opening_balance).toLocaleString() : '—'}</td>
                        <td style={{ ...td, color: (l.pnl || 0) > 0 ? '#7aab8a' : (l.pnl || 0) < 0 ? '#c47a7a' : 'rgba(240,237,230,0.4)' }}>{l.pnl != null ? (l.pnl >= 0 ? '+' : '') + '$' + Math.round(Math.abs(l.pnl)).toLocaleString() : '—'}</td>
                        <td style={{ ...td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'rgba(240,237,230,0.5)' }}>{l.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ marginBottom: '28px' }}>
        <div style={{ fontSize: '11px', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", marginBottom: '6px' }}>◈ Instructor View</div>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '32px', fontWeight: 500, color: '#c9a84c', margin: 0 }}>Instructor Dashboard</h1>
        <div style={{ fontSize: '13px', color: 'rgba(240,237,230,0.45)', fontFamily: "'Jost',sans-serif", marginTop: '4px' }}>Manage students, track progress, and grant access.</div>
      </div>

      {grantMsg && (
        <div style={{ background: 'rgba(122,171,138,0.1)', border: '1px solid rgba(122,171,138,0.3)', borderRadius: '8px', padding: '10px 16px', marginBottom: '16px', fontSize: '13px', color: '#7aab8a', fontFamily: "'Jost',sans-serif" }}>
          ✓ {grantMsg}
        </div>
      )}

      {loading ? (
        <div style={{ color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", fontSize: '13px', padding: '40px 0', textAlign: 'center' }}>Loading students…</div>
      ) : students.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '48px' }}>
          <div style={{ fontSize: '13px', color: 'rgba(240,237,230,0.4)', fontFamily: "'Jost',sans-serif", fontStyle: 'italic' }}>No students yet. When students sign up, they will appear here.</div>
        </div>
      ) : (
        <div style={card}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {['Name', 'Email', 'Joined', 'Chapters', 'Tier 2', 'Actions'].map(h => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {students.map(s => {
                const chapsDone = (s.student_progress || []).filter(p => p.completed).length;
                return (
                  <tr key={s.id}>
                    <td style={td}>{s.first_name || '—'}</td>
                    <td style={{ ...td, color: 'rgba(240,237,230,0.55)', fontSize: '11px' }}>{s.email}</td>
                    <td style={{ ...td, color: 'rgba(240,237,230,0.45)', whiteSpace: 'nowrap' }}>{new Date(s.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                    <td style={td}>{chapsDone}</td>
                    <td style={{ ...td, color: s.tier2_unlocked ? '#7aab8a' : 'rgba(240,237,230,0.3)' }}>{s.tier2_unlocked ? '✓' : '—'}</td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        <button style={btnSm('201,168,76')} onClick={() => viewStudentDetail(s)}>View Progress</button>
                        {!s.tier2_unlocked && (
                          <button style={btnSm('122,171,138')} onClick={() => grantTier2(s.id, s.first_name || s.email)}>Grant Tier 2</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export { AdminFeedbackView, AuthScreen }
export default AdminDashboard
